import React, { useEffect, useRef, useState } from 'react'
import posthog from 'posthog-js'
import { Trans, useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { formatDate } from '../../i18n/format'
import { ReferralStatus, referralShareText } from '../../utils/referral'
import { ReferralInvites, fetchReferralInvites, getReferralStatus } from '../../redux/actions/referralAction'
import { STORIES } from '../../routes/routeConstant'
import './refer-page.css'

const capture = (event: string, props: Record<string, any>) => {
  try {
    posthog.capture(event, props)
  } catch {
    /* analytics must never break the page */
  }
}

const STATUS_LABEL = {
  signed_up: 'referral:status.signedUp',
  on_trial: 'referral:status.onTrial',
  subscribed: 'referral:status.subscribed',
} as const

// The invite tracker: each label and the count it shows.
const STATS = [
  ['referral:referPage.stats.opened', 'clicks'],
  ['referral:referPage.stats.joined', 'signedUp'],
  ['referral:referPage.stats.onTrial', 'onTrial'],
  ['referral:referPage.stats.subscribed', 'subscribed'],
] as const

const Icon = ({ d }: { d: string }) => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d={d} />
  </svg>
)

// The terms behind the * on "2 weeks free". Mirrors the rules in dev-backend/src/core/referral.
const FINE_PRINT = [
  'referral:referPage.finePrint.credit',
  'referral:referPage.finePrint.newFriends',
  'referral:referPage.finePrint.limits',
  'referral:referPage.finePrint.websiteOnly',
]

// App payers can't be credited (the App Store / Google Play bill them), so their page is a gift.
const GIFT_STEPS = [
  {
    icon: 'M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v13',
    title: 'referral:referPage.steps.shareTitle',
    body: 'referral:referPage.steps.shareBody',
  },
  {
    icon: 'M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z',
    title: 'referral:referPage.steps.giftFriendTitle',
    body: 'referral:referPage.steps.giftFriendBody',
  },
]

const STEPS = [
  {
    icon: 'M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v13',
    title: 'referral:referPage.steps.shareTitle',
    body: 'referral:referPage.steps.shareBody',
  },
  {
    icon: 'M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z',
    title: 'referral:referPage.steps.friendTitle',
    body: 'referral:referPage.steps.friendBody',
  },
  {
    icon: 'M20 6 9 17l-5-5',
    title: 'referral:referPage.steps.rewardTitle',
    body: 'referral:referPage.steps.rewardBody',
  },
]

/**
 * /refer: the paying subscriber's invite link, the ways to share it, how it works, and the
 * tracker of who opened it, joined, started the trial and paid. Anyone who can't refer (Free,
 * lapsed, App Store billed) is sent back to their stories; the header only links here for those who can.
 */
const ReferPage: React.FC = () => {
  const { t } = useTranslation('referral')
  const [status, setStatus] = useState<ReferralStatus | null | undefined>(undefined)
  const [invites, setInvites] = useState<ReferralInvites | null>(null)
  const [copied, setCopied] = useState(false)
  const viewed = useRef(false)

  useEffect(() => {
    let alive = true
    getReferralStatus().then((s) => {
      if (!alive) return
      setStatus(s)
      if (s?.canRefer) fetchReferralInvites().then((i) => alive && setInvites(i))
    })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    if (status === undefined || viewed.current) return
    viewed.current = true
    capture('referral_page_viewed', { placement: 'refer_page', can_refer: !!status?.canRefer })
  }, [status])

  if (status === undefined) return <div className='refer-page' />

  // Referral only: paid and trialling website subscribers. Affiliates apply on www.maayot.com.
  if (!status?.canRefer || !status.link) return <Navigate to={STORIES} replace />

  const link = status.link
  const earnsReward = status.earnsReward !== false
  const text = referralShareText(link)
  const left = Math.max(status.maxRewardsPerYear - status.rewardsThisYear, 0)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* the link stays visible to copy by hand */
    }
    capture('referral_link_copied', { placement: 'refer_page' })
  }

  const shareTo = (method: string) => capture('referral_link_shared', { placement: 'refer_page', method })
  const nativeShare = async () => {
    shareTo('share_sheet')
    try {
      await navigator.share({ title: 'maayot', text, url: link })
    } catch {
      /* dismissed */
    }
  }
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  // Official brand marks (Simple Icons, CC0) in their brand colours; the envelope is a plain outline.
  const logo = (d: string, color: string) => (
    <svg width='26' height='26' viewBox='0 0 24 24' fill={color} aria-hidden='true'>
      <path d={d} />
    </svg>
  )
  const shares = [
    {
      method: 'whatsapp',
      label: t('referPage.share.whatsapp'),
      href: `https://wa.me/?text=${encodeURIComponent(text)}`,
      icon: logo(
        'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z',
        '#25D366',
      ),
    },
    {
      method: 'facebook',
      label: t('referPage.share.facebook'),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
      icon: logo(
        'M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z',
        '#0866FF',
      ),
    },
    {
      method: 'x',
      label: t('referPage.share.x'),
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
      icon: logo(
        'M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z',
        '#000000',
      ),
    },
    {
      method: 'email',
      label: t('referPage.share.email'),
      href: `mailto:?subject=${encodeURIComponent(t('referPage.emailSubject'))}&body=${encodeURIComponent(text)}`,
      icon: (
        <svg width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='#111a17' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <rect x='2' y='4' width='20' height='16' rx='2' />
          <path d='m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7' />
        </svg>
      ),
    },
  ]

  return (
    <div className='refer-page'>
      {earnsReward ? (
        <>
          <h1 className='refer-title'>
            <Trans t={t} i18nKey='referPage.rewardTitle' components={{ br: <br /> }} />
          </h1>
          <p className='refer-lead'>{t('referPage.rewardLead', { days: status.friendTrialDays })}</p>
        </>
      ) : (
        <>
          <h1 className='refer-title'>{t('referPage.giftTitle')}</h1>
          <p className='refer-lead'>{t('referPage.giftLead')}</p>
        </>
      )}

      <div className='refer-link'>
        <input value={link} readOnly aria-label={t('referPage.linkLabel')} onFocus={(e) => e.currentTarget.select()} />
      </div>
      <button type='button' className='refer-cta' onClick={copy}>
        {copied ? t('referPage.linkCopied') : t('referPage.copyLink')}
      </button>
      <div className='refer-share'>
        {shares.map((s) => (
          <a key={s.method} className='refer-share-btn' href={s.href} target='_blank' rel='noreferrer' aria-label={s.label} onClick={() => shareTo(s.method)}>
            {s.icon}
          </a>
        ))}
        {canShare && (
          <button type='button' className='refer-share-btn' aria-label={t('referPage.moreWaysToShare')} onClick={nativeShare}>
            <Icon d='M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v13' />
          </button>
        )}
      </div>

      <h2 className='refer-h2'>{t('referPage.howItWorks')}</h2>
      <ol className='refer-steps'>
        {(earnsReward ? STEPS : GIFT_STEPS).map((step) => (
          <li key={step.title} className='refer-step'>
            <span className='refer-step-icon'>
              <Icon d={step.icon} />
            </span>
            <div>
              <h3 className='refer-step-title'>{t(step.title)}</h3>
              <p className='refer-step-body'>{t(step.body)}</p>
            </div>
          </li>
        ))}
      </ol>

      <h2 className='refer-h2'>{t('referPage.yourInvites')}</h2>
      <div className='refer-stats'>
        {STATS.map(([label, field]) => (
          <div key={label} className='refer-stat'>
            <span className='refer-stat-value'>{invites?.[field] ?? 0}</span>
            <span className='refer-stat-label'>{t(label)}</span>
          </div>
        ))}
      </div>
      {invites?.friends?.length ? (
        <ul className='refer-friends'>
          {invites.friends.map((friend, i) => (
            <li key={i} className='refer-friend'>
              <span className='refer-friend-name'>{friend.name}</span>
              <span className='refer-friend-date'>{friend.joinedAt ? formatDate(friend.joinedAt, 'monthDay') : ''}</span>
              <span className={`refer-friend-status refer-friend-status--${friend.status}`}>
                {earnsReward && friend.rewarded
                  ? t('referPage.friendRewarded', { status: t(STATUS_LABEL[friend.status]) })
                  : t(STATUS_LABEL[friend.status])}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className='refer-empty'>{t('referPage.empty')}</p>
      )}
      {earnsReward && (
        <>
          <p className='refer-note'>
            {status.rewardsThisYear > 0
              ? t('referPage.rewardsEarned', { earned: status.rewardsThisYear, left })
              : t('referPage.rewardsCap', { max: status.maxRewardsPerYear })}
          </p>

          <h2 className='refer-h2 refer-h2--small'>{t('referPage.finePrintTitle')}</h2>
          <ul className='refer-fine-print'>
            {FINE_PRINT.map((line) => (
              <li key={line}>{t(line)}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

export default ReferPage
